import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import { MessageCircle, UserPlus } from "lucide-react";
import 'react-phone-input-2/lib/style.css';
import { Translator } from './translator';
import { useLanguage } from './LanguageContext';

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const Profile = () => {
  const LANG_MAP = {'ab':'Abkhazian','aa':'Afar','af':'Afrikaans','sq':'Albanian','am':'Amharic','ar':'Arabic','an':'Aragonese','hy':'Armenian','as':'Assamese','ae':'Avestan','ay':'Aymara','az':'Azerbaijani','ba':'Bashkir','eu':'Basque','be':'Belarusian','bn':'Bengali','bh':'Bihari','bi':'Bislama','bs':'Bosnian','br':'Breton','bg':'Bulgarian','my':'Burmese','ca':'Catalan','ch':'Chamorro','ce':'Chechen','zh':'Chinese','汉语':'Chinese','cu':'Church Slavic','cv':'Chuvash','kw':'Cornish','co':'Corsican','hr':'Croatian','cs':'Czech','da':'Danish','dv':'Divehi','nl':'Dutch','dz':'Dzongkha','en':'English','eo':'Esperanto','et':'Estonian','fo':'Faroese','fj':'Fijian','fi':'Finnish','fr':'French','gd':'Gaelic','gl':'Galician','ka':'Georgian','de':'German','el':'Greek','gn':'Guarani','gu':'Gujarati','ht':'Haitian Creole','ha':'Hausa','he':'Hebrew','hz':'Herero','hi':'Hindi','ho':'Hiri Motu','hu':'Hungarian','is':'Icelandic','io':'Ido','id':'Indonesian','ia':'Interlingua','ie':'Interlingue','iu':'Inuktitut','ik':'Inupiaq','ga':'Irish','it':'Italian','ja':'Japanese','jv':'Javanese','kl':'Kalaallisut','kn':'Kannada','ks':'Kashmiri','kk':'Kazakh','km':'Khmer','ki':'Kikuyu','rw':'Kinyarwanda','ky':'Kirghiz','kv':'Komi','ko':'Korean','kj':'Kuanyama','ku':'Kurdish','lo':'Lao','la':'Latin','lv':'Latvian','li':'Limburgish','ln':'Lingala','lt':'Lithuanian','lb':'Luxembourgish','mk':'Macedonian','mg':'Malagasy','ms':'Malay','ml':'Malayalam','mt':'Maltese','gv':'Manx','mi':'Maori','mr':'Marathi','mh':'Marshallese','mo':'Moldavian','mn':'Mongolian','na':'Nauru','nv':'Navajo','nd':'North Ndebele','nr':'South Ndebele','ng':'Ndonga','ne':'Nepali','se':'Northern Sami','no':'Norwegian','nb':'Norwegian Bokmål','nn':'Norwegian Nynorsk','ny':'Chichewa','oc':'Occitan','or':'Oriya','om':'Oromo','os':'Ossetian','pi':'Pali','pa':'Punjabi','fa':'Persian','pl':'Polish','pt':'Portuguese','ps':'Pashto','qu':'Quechua','rm':'Romansh','ro':'Romanian','rn':'Rundi','ru':'Russian','sm':'Samoan','sg':'Sango','sa':'Sanskrit','sc':'Sardinian','sr':'Serbian','sn':'Shona','ii':'Sichuan Yi','sd':'Sindhi','si':'Sinhala','sk':'Slovak','sl':'Slovenian','so':'Somali','st':'Southern Sotho', 'es':'Spanish','su':'Sundanese','sw':'Swahili','ss':'Swati','sv':'Swedish','tl':'Tagalog','ty':'Tahitian','tg':'Tajik','ta':'Tamil','tt':'Tatar','te':'Telugu','th':'Thai','bo':'Tibetan','ti':'Tigrinya','to':'Tonga','ts':'Tsonga','tn':'Tswana','tr':'Turkish','tk':'Turkmen','tw':'Twi','ug':'Uyghur','uk':'Ukrainian','ur':'Urdu','uz':'Uzbek','vi':'Vietnamese','vo':'Volapük','wa':'Walloon','cy':'Welsh','fy':'Western Frisian','wo':'Wolof','xh':'Xhosa','yi':'Yiddish','yo':'Yoruba','za':'Zhuang','zu':'Zulu'};
  const { translateText, lang } = Translator();
  const { setLang } = useLanguage();
  const fileInputRef = useRef(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [myCodes, setMyCodes] = useState([]);
  const formatCode = (c) => !c ? "" : (c.includes("-") ? c : `${c.slice(0,4)}-${c.slice(4,8)}`);
  const [subStatus, setSubStatus] = useState({ active:false, planKey:null, referralLimit:0, referralPrice:0 });
  const [friendEmail, setFriendEmail] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteErr, setInviteErr] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [inviteStage, setInviteStage] = useState("");
  const hasInviteFeedback = Boolean(inviteMsg || inviteErr);
  const [createMsg, setCreateMsg] = useState("");   // success info after create
  const [createErr, setCreateErr] = useState("");   // error info after create
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const baseTextByLang = {
    it: {
      text1: "Nessun dato utente trovato. Effettua nuovamente l'accesso.",
      text2: "Email o password non valide. Effettua nuovamente l'accesso.",
      text3: "Profilo aggiornato con successo.",
      text4: "Errore durante l'aggiornamento. Riprova più tardi.",
      text5: "Impossibile aggiornare la foto. Accedi di nuovo.",
      text6: "Foto profilo aggiornata con successo.",
      text7: "Errore durante l'upload della foto. Riprova più tardi.",
      text8: "Nome",
      text9: "Cognome",
      text10: "Telefono",
      text11: "Indirizzo",
      text12: "CAP",
      text13: "Nazione",
      text14: "Email",
      text15: "Password",
      text16: "Salva Profilo",
      text17: "Genere",
      text18: "Lingua preferita",
      text19: "Questo è il Codice Sconto per i tuoi amici",
      text199: "Invia lo sconto al tuo amico",
      text20: "Piano",
      text21: "invii consentiti",
      text22: "Non hai ancora creato nessun codice.",
      text23: "Copia",
      text24: "Crea codice scontato",
      text25: "Mio Profilo",
      text26: "Per iniziare crea un nuovo codice sconto.",

      textabonamento: "Abbonamento:",
      textActive: "Attivo",
      textalert1: "Errore creazione codice",
      textmale: "Maschio",
      textfemale: "Femmina",
      textprefer_not_to_say: "Preferisco non dirlo",
      shareLink: "Copia link",

      textTrov: "Nessun dato utente trovato.",
      textFriendEmail: "Inserire l'email dell'amico",
      textSendEmail: "Invia email",
      textInviteSent: "L'email inviata!",
      textInviteError: "Impossibile inviare l’invito. Riprova.",
      textInvalidEmail: "Inserisci un'email valida.",
      textCantInviteSelf: "Non puoi invitare te stesso.",

      textwrong: "Codice esaurito. Crea un nuovo codice.",

      textTabProfile: "Informazioni personali",
      textTabNetwork: "Amici e networking",
      textTabReferrals: "Codici sconto e inviti",
      textYourContacts: "I tuoi contatti",
      textNewFriendRequests: "Nuove richieste di amicizia",
      textAllFriends: "Tutti i contatti",
      textNoFriendRequests: "Nessuna nuova richiesta di amicizia.",
      textNoFriends: "Nessun contatto ancora.",

      textCaricamento: "Caricamento...",
      textPhotoHint: "Clicca sul cerchio per caricare la tua foto profilo",
      textCodeCreated: "Codice creato:",
      textScadeIl: "Scade il",
      textScade: "Scade:",
      textFirstMonth: "Prima mensilità:",
      textRemaining: "Rimasti:",
      textMessagesBtn: "Messaggi",
    },

    en: {
      text1: "No user data found. Please log in again.",
      text2: "Invalid email or password. Please log in again.",
      text3: "Profile updated successfully.",
      text4: "Error while updating. Please try again later.",
      text5: "Unable to update photo. Please log in again.",
      text6: "Profile photo updated successfully.",
      text7: "Error uploading the photo. Please try again later.",
      text8: "First name",
      text9: "Last name",
      text10: "Phone",
      text11: "Address",
      text12: "ZIP code",
      text13: "Country",
      text14: "Email",
      text15: "Password",
      text16: "Save profile",
      text17: "Gender",
      text18: "Preferred language",
      text19: "This is the Discount Code for your friends",
      text199: "Send the discount to your friend",
      text20: "Plan",
      text21: "allowed sends",
      text22: "You haven't created any codes yet.",
      text23: "Copy",
      text24: "Create discount code",
      text25: "My Profile",
      text26: "To get started, create a new discount code.",

      textabonamento: "Subscription:",
      textActive: "Active",
      textalert1: "Error creating code",
      textmale: "Male",
      textfemale: "Female",
      textprefer_not_to_say: "Prefer not to say",
      shareLink: "Copy link",

      textTrov: "No user data found.",
      textFriendEmail: "Enter your friend's email",
      textSendEmail: "Send email",
      textInviteSent: "Email sent!",
      textInviteError: "Unable to send invitation. Please try again.",
      textInvalidEmail: "Please enter a valid email.",
      textCantInviteSelf: "You cannot invite yourself.",

      textwrong: "Code fully used. Create a new code.",

      textTabProfile: "Personal information",
      textTabNetwork: "Friends and networking",
      textTabReferrals: "Discount codes and invites",
      textYourContacts: "Your contacts",
      textNewFriendRequests: "New friend requests",
      textAllFriends: "All contacts",
      textNoFriendRequests: "No new friend requests.",
      textNoFriends: "No contacts yet.",

      textCaricamento: "Loading...",
      textPhotoHint: "Click on the circle to upload your profile picture",
      textCodeCreated: "Code created:",
      textScadeIl: "Expires on",
      textScade: "Expires:",
      textFirstMonth: "First month:",
      textRemaining: "Remaining:",
      textMessagesBtn: "Messages",
    },

    fr: {
      text1: "Aucune donnée utilisateur trouvée. Veuillez vous reconnecter.",
      text2: "Email ou mot de passe invalide. Veuillez vous reconnecter.",
      text3: "Profil mis à jour avec succès.",
      text4: "Erreur lors de la mise à jour. Réessayez plus tard.",
      text5: "Impossible de mettre à jour la photo. Connectez-vous à nouveau.",
      text6: "Photo de profil mise à jour avec succès.",
      text7: "Erreur lors du téléversement de la photo. Réessayez plus tard.",
      text8: "Prénom",
      text9: "Nom",
      text10: "Téléphone",
      text11: "Adresse",
      text12: "Code postal",
      text13: "Pays",
      text14: "Email",
      text15: "Mot de passe",
      text16: "Enregistrer le profil",
      text17: "Genre",
      text18: "Langue préférée",
      text19: "Voici le code de réduction pour vos amis",
      text199: "Envoyer la réduction à votre ami",
      text20: "Forfait",
      text21: "envoyés autorisés",
      text22: "Vous n'avez encore créé aucun code.",
      text23: "Copier",
      text24: "Créer un code de réduction",
      text25: "Mon profil",
      text26: "Pour commencer, créez un nouveau code de réduction.",

      textabonamento: "Abonnement :",
      textActive: "Actif",
      textalert1: "Erreur lors de la création du code",
      textmale: "Homme",
      textfemale: "Femme",
      textprefer_not_to_say: "Je préfère ne pas le dire",
      shareLink: "Copier le lien",

      textTrov: "Aucune donnée utilisateur trouvée.",
      textFriendEmail: "Saisir l'email de l'ami",
      textSendEmail: "Envoyer l'email",
      textInviteSent: "Email envoyé !",
      textInviteError: "Impossible d’envoyer l’invitation. Réessayez.",
      textInvalidEmail: "Veuillez saisir un email valide.",
      textCantInviteSelf: "Vous ne pouvez pas vous inviter vous-même.",

      textwrong: "Code épuisé. Créez un nouveau code.",

      textTabProfile: "Informations personnelles",
      textTabNetwork: "Amis et réseau",
      textTabReferrals: "Codes de réduction et invitations",
      textYourContacts: "Vos contacts",
      textNewFriendRequests: "Nouvelles demandes d’ami",
      textAllFriends: "Tous les contacts",
      textNoFriendRequests: "Aucune nouvelle demande d’ami.",
      textNoFriends: "Aucun contact pour le moment.",

      textCaricamento: "Chargement...",
      textPhotoHint: "Cliquez sur le cercle pour téléverser votre photo de profil",
      textCodeCreated: "Code créé :",
      textScadeIl: "Expire le",
      textScade: "Expire :",
      textFirstMonth: "Premier mois :",
      textRemaining: "Restants :",
      textMessagesBtn: "Messages",
    },
  };

  const baseText = baseTextByLang.it;

  const [translatedText, setTranslatedText] = useState(baseText);

  useEffect(() => {
    let cancelled = false;

    // 1) Direct languages: it / en / fr
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang] || baseTextByLang.it);
      return () => {
        cancelled = true;
      };
    }

    // 2) Any other language (e.g. "ar") → translate from Italian
    async function translateAll() {
      try {
        const entries = Object.entries(baseTextByLang.it);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value];
          })
        );

        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch (err) {
        if (!cancelled) {
          // Fallback to Italian if translation fails
          setTranslatedText(baseTextByLang.it);
        }
      }
    }

    translateAll();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);

  // detect expiration of PLAN
  const nowMs = Date.now();

  const isExpired =
    subStatus?.expiresAt && typeof subStatus.expiresAt === "number"
      ? subStatus.expiresAt <= nowMs
      : false;

  // Uses left across all existing codes (for display)
  const invitesLeft = myCodes.reduce((acc, c) => acc + Math.max(0, (c.usesLeft ?? ((c.maxUses || 0) - (c.used || 0)))), 0);

  // Eligibility and ability to mint a new code
  const eligible = subStatus.active && !isExpired && (subStatus.planKey === "BASIC" || subStatus.planKey === "PLUS");

  const primaryCode = React.useMemo(() => { if (!eligible || !myCodes?.length) return null; return myCodes.find(c => (c.usesLeft ?? ((c.maxUses||0)-(c.used||0))) > 0) || myCodes[0];}, [eligible, myCodes]);
  const prettyCode = (c) => !c ? "" : (c.includes("-") ? c : `${c.slice(0,4)}-${c.slice(4)}`);
  const shareUrlFor = (c) => `https://techetour.it/carrello?plan=${encodeURIComponent(c.planKey)}&code=${encodeURIComponent(c.code)}`;

  // Normalize display values: never show "N/A", null, or undefined
  const safeValue = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string" && v.trim().toUpperCase() === "N/A") return "";
    return v;
  };

  const friendDisplayName = (obj) =>
    safeValue(obj?.name) ||
    safeValue(obj?.fullName) ||
    safeValue(obj?.email) ||
    "";



  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    indirizzo: '',
    nazione: '',
    cap: '',
    email: '',
    password: '',
    gender: '',
    preferredLanguage: ''

  });

  const [profilePic, setProfilePic] = useState('/avatar.png');
  
  // "profile" | "network"
  const [activeTab, setActiveTab] = useState("profile");
  const [showPhotoHint, setShowPhotoHint] = useState(false);
  
  // plan-derived UI flags (treat expired as inactive)
  const planKey     = (subStatus?.planKey || '').toUpperCase();
  const isActive    = !!subStatus?.active && !isExpired;
  const isGoldUI    = isActive && planKey === 'PLUS';
  const isSilverUI  = isActive && planKey === 'BASIC';
  const showSubText = isActive && (planKey === 'BASIC' || planKey === 'PLUS');
  const BADGE_SRC   = { PLUS: '/badges/PLUS.png', BASIC: '/badges/BASIC.png' };

  const showBadge   = isActive && (planKey === 'PLUS' || planKey === 'BASIC');
  const badgeSrc  = BADGE_SRC[planKey];

  const planTextClass = isGoldUI
  ? 'bg-gradient-to-b from-[#FFD680] to-[#E39B20] text-transparent bg-clip-text drop-shadow-[0_2px_8px_rgba(227,155,32,0.45)]'
  : isSilverUI
  ? 'bg-gradient-to-b from-[#ECECEC] to-[#AFAFAF] text-transparent bg-clip-text drop-shadow-[0_2px_8px_rgba(160,160,160,0.45)]'
  : 'text-white';

// inputs/selects borders & focus (gold for PLUS, silver for BASIC)
const inputBorderClass = isGoldUI
  ? 'border-[#FFC54D] ring-1 ring-[#FFC54D] shadow-[0_0_0_2px_rgba(255,197,77,0.45)]'
  : isSilverUI
  ? 'border-[#C0C0C0] ring-1 ring-[#C0C0C0] shadow-[0_0_0_2px_rgba(192,192,192,0.45)]'
  : 'border-white';

const inputFocusClass = isGoldUI
  ? 'focus:ring-2 focus:ring-[#FFC54D] focus:shadow-[0_0_10px_rgba(255,197,77,0.6)]'
  : isSilverUI
  ? 'focus:ring-2 focus:ring-[#C0C0C0] focus:shadow-[0_0_10px_rgba(192,192,192,0.6)]'
  : 'focus:border-indigo-600 focus:shadow-[0_0_8px_rgba(75,0,130,0.4)]';

// avatar ring (gold for PLUS, silver for BASIC)
const avatarRingClass = isGoldUI
  ? 'ring-8 ring-[#FFC54D] shadow-[0_0_20px_rgba(255,197,77,0.7)]'
  : isSilverUI
  ? 'ring-8 ring-[#C0C0C0] shadow-[0_0_20px_rgba(192,192,192,0.7)]'
  : '';



  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) { refreshReferralData(); }
  }, []);

  const createReferralCode = async (planKey) => {
    setCreateMsg(""); setCreateErr("");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user?.email) {
      setCreateErr(translatedText.text1);
      return;
    }
    try {
      const res = await axios.post(`${BASE}/discount_create`, { email: user.email, planKey });
      if (res.data.status === "success") {
        const pretty = `${res.data.code.slice(0,4)}-${res.data.code.slice(4)}`;
        const expiresStr = new Date(res.data.expiresAt).toLocaleDateString(lang || undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
        setCreateMsg(`${translatedText.textCodeCreated} ${pretty} • ${translatedText.textScadeIl} ${expiresStr}`);
        await refreshReferralData(); // refresh list & limits
      } else {
        setCreateErr(res.data.message || translatedText.textalert1);
      }
    } catch {
      setCreateErr(translatedText.textalert1);
    }
  };


  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      setForm(prev => ({
        ...prev,
        email: userData.email || '',
        password: userData.password || '',
        nome: userData.firstName || '',
        cognome: userData.lastName || '',
        telefono: userData.telefono || '',
        indirizzo: userData.indirizzo || '',
        nazione: userData.nazione || '',
        cap: userData.cap || '',
        gender: userData.gender || '',
        preferredLanguage: userData.preferredLanguage || ''
      }));

      const rawPic = userData.profilePic;

      // If no image in DB or placeholder/invalid → use default avatar
      if (
        !rawPic ||
        rawPic === "./avatar.png" ||
        rawPic === "../avatar.png" ||
        rawPic === "/avatar.png" ||
        rawPic === "undefined" ||
        rawPic === "null"
      ) {
        setProfilePic("/avatar.png");
      } else {
        // If it's already a full URL, use as-is
        const fullPicUrl = rawPic.startsWith("http")
          ? rawPic
          : `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${String(
              rawPic
            ).replace(/^\/+/, "")}`;

        setProfilePic(fullPicUrl);
      }
    }
  }, []);


  // Show "click the circle to upload photo" popup for 5 seconds
  useEffect(() => {
    // only show if we are on the default avatar
    if (!profilePic || profilePic.includes("/avatar.png")) {
      setShowPhotoHint(true);
      const t = setTimeout(() => setShowPhotoHint(false), 5000); // 5 sec
      return () => clearTimeout(t);
    }
  }, [profilePic]);




  async function refreshReferralData() {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u?.email) return;

    // Plan details from active subscription (not cart)
    const ss = await fetch(`${BASE}/subscription_status?email=${encodeURIComponent(u.email)}`).then(r=>r.json()).catch(()=>null);
    if (ss?.status === "success") setSubStatus(ss);

    // call getCart to use planInfo for UI
    const cartRes = await fetch(`${BASE}/getCart?email=${encodeURIComponent(u.email)}`).then(r=>r.json()).catch(()=>null);
    setPlanInfo(cartRes?.plan || null);

    // List my codes
    const codesRes = await fetch(`${BASE}/discount_my_codes?email=${encodeURIComponent(u.email)}`).then(r=>r.json()).catch(()=>null);
    if (codesRes?.status === "success") setMyCodes(codesRes.codes || []);

    // Load my contacts + pending friend requests
    await loadContacts(u.email);
  }



  async function loadContacts(email) {
    try {
      setContactsLoading(true);

      const friendsRes = await fetch(
        `${BASE}/friends_list?email=${encodeURIComponent(email)}`
      ).then(r => r.json()).catch(() => null);

      const requestsRes = await fetch(
        `${BASE}/friend_requests?email=${encodeURIComponent(email)}`
      ).then(r => r.json()).catch(() => null);

      setFriends(
        (friendsRes && (friendsRes.friends || friendsRes)) || []
      );
      setFriendRequests(
        (requestsRes && (requestsRes.requests || requestsRes)) || []
      );
    } catch (e) {
      console.error("Error loading contacts", e);
    } finally {
      setContactsLoading(false);
    }
  }



  // Open ChatFloat DM with a specific friend (fallback: just open chat list)
  const openMessagesForFriend = (friendObj) => {
    try {
      const me = JSON.parse(localStorage.getItem("user") || "null");
      if (!me?.email) {
        window.dispatchEvent(new CustomEvent("chat:open", { detail: {} }));
        return;
      }

      const convId =
        friendObj?.convId ||
        friendObj?.conversationId ||
        friendObj?.dmKey ||
        null;

      if (convId) {
        window.dispatchEvent(
          new CustomEvent("chat:open", { detail: { convId } })
        );
      } else {
        window.dispatchEvent(new CustomEvent("chat:open", { detail: {} }));
      }
    } catch {
      window.dispatchEvent(new CustomEvent("chat:open", { detail: {} }));
    }
  };





  // SEND REFERRAL INVITE
  const sendReferralInvite = async () => {
    setInviteMsg(""); setInviteErr("");

    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user?.email) { setInviteErr(translatedText.textTrov); return; }
    if ((friendEmail || "").trim().toLowerCase() === user.email.toLowerCase()) {
      setInviteErr(translatedText.textCantInviteSelf || "Non puoi invitare te stesso.");
      return;
    }
    if (!EMAIL_RX.test(friendEmail)) {
      alert(translatedText.textInvalidEmail);
      return;
    }

    // send the code that’s shown in the UI
    const codeRaw = (primaryCode?.code || "").replace(/-/g, "");

    const payload = {
      ownerEmail: user.email,
      friendEmail,
      planKey: (subStatus?.planKey || "").toUpperCase(),
      code: codeRaw,
      shareUrl: shareUrlFor(primaryCode || {
        planKey: (subStatus?.planKey || "").toUpperCase(),
        code: primaryCode?.code || ""
      }),
    };

    try {
      setSendingInvite(true);
      const r2 = await axios.post(`${BASE}/send_referral_email`, payload);
      if (r2?.data?.status === "success") {
        alert(translatedText.textInviteSent);
        setFriendEmail("");
        refreshReferralData();
      } else {
        setInviteErr(r2?.data?.message || translatedText.textInviteError);
      }
    } catch {
      setInviteErr(translatedText.textInviteError);
    } finally {
      setSendingInvite(false);
    }
  };





  
  


  



  


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };


  const handleSave = async (e) => {
    e.preventDefault();
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) { alert(translatedText.text1); return; }

        const cleanForm = Object.fromEntries(
      Object.entries(form).map(([key, value]) => {
        if (value === null || value === undefined) return [key, ""];
        if (typeof value === "string" && value.trim().toUpperCase() === "N/A") {
          return [key, ""];
        }
        return [key, value];
      })
    );

    try {
      const url =
        `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/changeTUserData` +
        `?email=${encodeURIComponent(userData.email)}` +
        `&password=${encodeURIComponent(userData.password)}` +
        `&newfirstname=${encodeURIComponent(cleanForm.nome)}` +
        `&newlastname=${encodeURIComponent(cleanForm.cognome)}` +
        `&newphone=${encodeURIComponent(cleanForm.telefono)}` +
        `&newaddress=${encodeURIComponent(cleanForm.indirizzo)}` +
        `&newcap=${encodeURIComponent(cleanForm.cap)}` +
        `&newcountry=${encodeURIComponent(cleanForm.nazione)}` +
        `&newgender=${encodeURIComponent(cleanForm.gender)}` +
        `&newlang=${encodeURIComponent(cleanForm.preferredLanguage)}`;

      const response = await fetch(url, { method: "GET" });
      const result = await response.json();

      if (result === "0") {
        alert(translatedText.text2);
      } else {
        alert(translatedText.text3);

        const updatedUser = {
          ...userData,
          firstName: cleanForm.nome,
          lastName: cleanForm.cognome,
          telefono: cleanForm.telefono,
          indirizzo: cleanForm.indirizzo,
          cap: cleanForm.cap,
          nazione: cleanForm.nazione,
          gender: cleanForm.gender,
          preferredLanguage: cleanForm.preferredLanguage,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        const refreshedUserData = JSON.parse(localStorage.getItem("user"));
        setForm(prev => ({
          ...prev,
          nome: refreshedUserData.firstName || "",
          cognome: refreshedUserData.lastName || "",
          telefono: refreshedUserData.telefono || "",
          indirizzo: refreshedUserData.indirizzo || "",
          cap: refreshedUserData.cap || "",
          nazione: refreshedUserData.nazione || "",
          email: refreshedUserData.email || "",
          password: refreshedUserData.password || "",
        }));

        setLang(cleanForm.preferredLanguage || "it");
      }
    } catch (error) {
      alert(translatedText.text4);
    }
  };




const handleFileChange = async (e) => {
  try {
    console.log("[avatar] onChange fired");
    const file = e.target.files?.[0];
    console.log("[avatar] picked file:", file && { name:file.name, size:file.size, type:file.type });
    if (!file) return;

    const userData = JSON.parse(localStorage.getItem("user") || "null");
    console.log("[avatar] userData:", userData);
    if (!userData) { alert(translatedText.text1); return; }

    const formData = new FormData();
    formData.append("email", userData.email);
    formData.append("password", userData.password);
    formData.append("newPhoto", file, file.name);

    console.log("[avatar] about to POST");
    const response = await fetch("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/changeTUserDataPhoto", {
      method: "POST",
      body: formData,
    });
    console.log("[avatar] response status:", response.status);

    const result = await response.json().catch(()=>null);
    console.log("[avatar] response json:", result);

    if (!response.ok || !result || result === "0" || !result.newPhotoPath) {
      alert(translatedText.text7); // "Errore durante l'upload…"
      return;
    }

    const updatedPicUrl = `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${String(result.newPhotoPath).replace(/^\/+/, "")}`;
    // bust cache so you see the new picture
    setProfilePic(`${updatedPicUrl}?t=${Date.now()}`);

    const user = JSON.parse(localStorage.getItem("user") || "null") || {};
    localStorage.setItem("user", JSON.stringify({ ...user, profilePic: updatedPicUrl }));

    const bustedUrl = `${updatedPicUrl}?t=${Date.now()}`;

    // update local <img>
    setProfilePic(bustedUrl);

    // 🔔 tell the rest of the app (Header) that the avatar changed
    window.dispatchEvent(
      new CustomEvent("auth:avatarChanged", { detail: { profilePic: bustedUrl } })
    );

  } catch (err) {
    console.error("[avatar] UPLOAD ERROR:", err);
    alert(translatedText.text7);
  }
};




  const formatAmount = (n) =>
    new Intl.NumberFormat(lang || undefined, { style: "currency", currency: "EUR" }).format((n ?? 0));

  const formatDate = (d) =>
    new Date(d).toLocaleDateString(lang || undefined);


  return (
    <>
    <style>
  {`
    /* Wrapper of the phone input in Profile */
    .phone-input-profile {
      border: 1px solid #ffffff;
      border-radius: 0.75rem;
      background-color: #324B8E;
      padding-left: 0.25rem;
      width: 100%;
    }

    /* Move flag a bit to the right, remove its own border */
    .phone-input-profile .flag-dropdown {
      left: 0.75rem;
      border: none !important;
    }

    /* Base state for the flag area */
    .phone-input-profile .selected-flag {
      background-color: #324B8E;          /* same blue as background */
      border-radius: 0.75rem 0 0 0.75rem;
    }

    /* hover  */
    .phone-input-profile .selected-flag:hover,
    .phone-input-profile .selected-flag:focus,
    .phone-input-profile .flag-dropdown.open .selected-flag {
      background-color: #324B8E !important;
    }

    /* Actual input: no border, no shadow, text pushed after flag */
    .phone-input-profile .form-control {
      border: none !important;
      box-shadow: none !important;
      background: transparent;
      color: #ffffff;
      padding-left: 3.2rem;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
  `}
</style>

    
    
    <div className="bg-[#324B8E] min-h-screen flex flex-col items-center py-10">
      
      {/* Header */}
      <h1 className="text-white text-4xl font-bold mb-6 self-start pl-16">{translatedText.text25}</h1>




      {/* Tabs: Personal info / Friends & networking */}
      <div className="self-start pl-16 mb-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setActiveTab("profile")}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition
            ${activeTab === "profile"
              ? "bg-white text-[#324B8E] border-white shadow"
              : "bg-transparent text-white border-white/40 hover:bg-white/10"}`}
        >
          {translatedText.textTabProfile}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("network")}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition
            ${activeTab === "network"
              ? "bg-white text-[#324B8E] border-white shadow"
              : "bg-transparent text-white border-white/40 hover:bg-white/10"}`}
        >
          {translatedText.textTabNetwork}
        </button>
      </div>






      {/* ---------- Header strip with left text / center avatar / right badge ---------- */}
      <div className="w-full max-w-screen-xl px-4 md:px-10 grid grid-cols-1 md:grid-cols-3 items-center gap-6 mb-10">
        {/* LEFT TEXT: only when active PLAN */}
        {showSubText ? (
          <div className="text-white md:justify-self-start">
            <div className="text-3xl opacity-80">{translatedText.textabonamento}</div>
              <div className={`text-5xl font-extrabold leading-tight ${planTextClass}`}>
                {planKey}
              </div>
            <div className="text-3xl opacity-80">({translatedText.textActive})</div>
          </div>
        ) : <div />}

        {/* CENTER: Avatar (click to upload) */}
        <div className="relative md:justify-self-center mb-6 flex flex-col items-center">
          {/* 5-second popup hint, like a small pill */}
          {showPhotoHint && !profilePic.includes("/avatar.png") === false && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-white shadow-md border border-gray-300 text-xs text-gray-800 absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-10">
              <span>{translatedText.textPhotoHint}</span>
              {/* Speech bubble tail */}
              <div
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-b border-gray-300 rotate-45"
              />
            </div>
          )}

          <img
            src={profilePic}
            alt="Profile"
            className={`w-48 h-48 rounded-full object-cover cursor-pointer ${avatarRingClass}`}
            onClick={() => fileInputRef.current.click()}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>


        {/* RIGHT BADGE: only when active PLAN */}
        {showBadge ? (
          <div className="md:justify-self-end">
            <img
              src={badgeSrc}
              alt={`${planKey} badge`}
              className="w-48 h-48 object-contain drop-shadow-[0_10px_25px_rgba(0,0,0,0.25)]"
            />
          </div>
        ) : <div />}
      </div>











{/* ---- Personal information tab ---- */}
{activeTab === "profile" && (
<>
{/* ---------- Referral / Discount widget ---------- */}
{eligible && (
  <div className="w-full max-w-screen-xl px-4 md:px-10 mb-8">
    <div className="rounded-2xl border border-white/30 bg-white/10 backdrop-blur p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-white text-xl font-semibold">{translatedText.text19}</h2>
      </div>

      {/* Highlighted main code */}
      {primaryCode ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          {/* Big code */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2">
              <div className="text-white text-2xl font-mono tracking-[0.3em]">
                {primaryCode.code}
              </div>

              {/* Small copy icon (copies the code) */}
              <button
                onClick={() => navigator.clipboard.writeText(primaryCode.code)}
                className="p-1 rounded-lg border border-white/30 text-white hover:bg-white/10 transition"
                type="button"
                title={translatedText.text23 /* Copia */}
                aria-label={translatedText.text23}
              >

                {/* SVG icon */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                    fill="currentColor" className="w-4 h-4">
                  <path d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </button>

              {/* link-copy icon */}
              <button
                onClick={() => navigator.clipboard.writeText(shareUrlFor(primaryCode))}
                className="p-1 rounded-lg border border-white/30 text-white hover:bg-white/10 transition"
                type="button"
                title={translatedText.shareLink /* Copia link */}
                aria-label={translatedText.shareLink}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                    fill="currentColor" className="w-4 h-4">
                  <path d="M3.9 12a4.1 4.1 0 0 1 4.1-4.1h3v2h-3A2.1 2.1 0 1 0 8 14h3v2H8A4.1 4.1 0 0 1 3.9 12zm6-1h4v2h-4v-2zm6.1-3.1A4.1 4.1 0 0 1 20.1 12 4.1 4.1 0 0 1 16 16.1h-3v-2h3a2.1 2.1 0 1 0 0-4.2h-3v-2h3z"/>
                </svg>
              </button>
              
            </div>

            <div className="text-xs text-white/70 mt-1">
              {primaryCode?.expiresAt ?(<>{translatedText.textScade} {formatDate(primaryCode.expiresAt)}</>) : null}
            </div>
          </div>

          {/* Meta */}
          <div className="md:col-span-1 text-white/90">
            <div className="text-sm">
              {translatedText.textFirstMonth}{" "}
              <b className="text-white">€{(primaryCode.amount ?? 0).toFixed(2)}</b>
            </div>
            <div className="text-sm">
              {translatedText.textRemaining}{" "}
              <b className="text-white">{primaryCode.usesLeft}</b>
            </div>
          </div>

          {/* Actions - Email invite */}
          <div className="md:col-span-1 flex flex-col sm:flex-row gap-2 md:justify-end">

            {/* Title above the email input */}
            <div className="w-full sm:w-auto sm:flex-1">
              <label
                htmlFor="friend-email"
                className="block text-white/90 text-sm mb-1"
              >
                {translatedText.text199}
              </label>

              {/* --- Show email sent msg --- */}
              {inviteMsg && (
                <div
                  className="mb-2 px-3 py-2 rounded-xl border bg-emerald-500/15 text-emerald-100 border-emerald-400/30 text-sm"
                  aria-live="polite"
                >
                  {inviteMsg}
                </div>
              )}
              {inviteErr && (
                <div
                  className="mb-2 px-3 py-2 rounded-xl border bg-rose-500/15 text-rose-100 border-rose-400/30 text-sm"
                  aria-live="polite" 
                >
                  {inviteErr}
                </div>
              )}

              {/* Email + send remain visible */}
              <div className="relative w-full">
                <input
                  id="friend-email"
                  type="email"
                  value={friendEmail}
                  onChange={(e) => {
                    setFriendEmail(e.target.value);
                    if (inviteMsg) setInviteMsg("");
                    if (inviteErr) setInviteErr("");
                  }}
                  placeholder={translatedText.textFriendEmail}
                  className={`bg-[#324B8E] w-full text-sm leading-6 px-3 py-2 pr-10 rounded-xl border ${inputBorderClass} text-white outline-none transition ${inputFocusClass}`}
                />

                {/* Icon button INSIDE input */}
                <button
                  onClick={sendReferralInvite}
                  disabled={sendingInvite}
                  title={translatedText.textSendEmail}
                  aria-label={translatedText.textSendEmail}
                  type="button"
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md ${
                    isGoldUI
                      ? 'bg-gradient-to-b from-[#FFD680] to-[#E39B20]'
                      : 'bg-[#49B15D]'
                  } text-white hover:opacity-90 disabled:opacity-60 transition`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="white"
                    className="w-4 h-4"
                  >
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // No code yet
        <div className="mt-3 text-sm text-white/80">
          {translatedText.text22}<br/>{translatedText.text26}
        </div>
      )}
    </div>
  </div>
)}
</>
)}




      {/* ---- Friends & networking tab ---- */}
      {activeTab === "network" && (
        <div className="w-full max-w-screen-xl px-4 md:px-10 mb-8">
          <h2 className="text-white text-2xl font-semibold mb-4">
            {translatedText.textYourContacts}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* New friend requests */}
            <div className="rounded-2xl border border-white/25 bg-white/10 backdrop-blur p-4 md:p-5">
              <h3 className="text-white text-lg font-semibold mb-3">
                {translatedText.textNewFriendRequests}
              </h3>

              {contactsLoading ? (
                <div className="text-white/70 text-sm">{translatedText.textCaricamento}</div>
              ) : friendRequests.length === 0 ? (
                <div className="text-white/70 text-sm">
                  {translatedText.textNoFriendRequests}
                </div>
              ) : (
                <ul className="space-y-3">
                  {friendRequests.map((req, i) => {
                    const displayName = friendDisplayName(req);
                    const initial = displayName
                      ? displayName.toString().trim().charAt(0).toUpperCase()
                      : "";

                    return (
                      <li
                        key={req.id || req.email || i}
                        className="flex items-center gap-3 rounded-xl bg-[#324B8E]/80 border border-white/15 px-3 py-2"
                      >
                        <div className="w-10 h-10 rounded-full bg-white/20 overflow-hidden flex items-center justify-center text-white font-semibold">
                          {req.avatar || req.image ? (
                            <img
                              src={req.avatar || req.image || "/avatar.png"}
                              alt={displayName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            initial
                          )}
                        </div>


                        <div className="flex-1">
                          <div className="text-white text-sm font-medium">
                            {displayName}
                          </div>
                          {safeValue(req.email) && (
                            <div className="text-white/60 text-xs">
                              {safeValue(req.email)}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Friends list with Messages button */}
            <div className="rounded-2xl border border-white/25 bg-white/10 backdrop-blur p-4 md:p-5">
              <h3 className="text-white text-lg font-semibold mb-3">
                {translatedText.textAllFriends}
              </h3>

              {contactsLoading ? (
                <div className="text-white/70 text-sm">{translatedText.textCaricamento}</div>
              ) : friends.length === 0 ? (
                <div className="text-white/70 text-sm">
                  {translatedText.textNoFriends}
                </div>
              ) : (
                <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {friends.map((f, i) => {
                    const displayName = friendDisplayName(f);
                    const initial = displayName
                      ? displayName.toString().trim().charAt(0).toUpperCase()
                      : "";

                    return (
                      <li
                        key={f.id || f.email || i}
                        className="flex items-center gap-3 rounded-xl bg-[#324B8E]/80 border border-white/15 px-3 py-2"
                      >
                        <div className="w-10 h-10 rounded-full bg-white/20 overflow-hidden flex items-center justify-center text-white font-semibold">
                          {f.avatar || f.image ? (
                            <img
                              src={f.avatar || f.image || "/avatar.png"}
                              alt={displayName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            initial
                          )}
                        </div>


                        <div className="flex-1">
                          <div className="text-white text-sm font-medium">
                            {displayName}
                          </div>
                          {safeValue(f.lastMessage) && (
                            <div className="text-white/60 text-xs truncate">
                              {safeValue(f.lastMessage)}
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => openMessagesForFriend(f)}
                          className="px-3 py-1 text-xs rounded-lg bg-[#49B15D] text-white hover:opacity-90 transition flex items-center gap-1"
                        >
                          <MessageCircle className="w-3 h-3" />
                          <span>{translatedText.textMessagesBtn}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}







      
{/* ---- Personal information tab ---- */}
{activeTab === "profile" && (
<>
      {/* Form */}
      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-screen-xl px-4 md:px-10">
        
        {[
          { label: translatedText.text8, name: "nome" },
          { label: translatedText.text9, name: "cognome" },
          { label: translatedText.text10, name: "telefono" },
          { label: translatedText.text11, name: "indirizzo" },
          { label: translatedText.text12, name: "cap", type: "text", maxLength: 8, onlyNumbers: true },
          { label: translatedText.text13, name: "nazione" },
          { label: translatedText.text14, name: "email", readOnly: true },
          { label: translatedText.text15, name: "password", type: "password", readOnly: true }
        ].map((field, idx) => (
          <div key={idx} className="mt-6 flex flex-col">
            <label className="text-white mb-2">{field.label}</label>

            {field.name === "telefono" ? (
            <PhoneInput
              country="it"
              value={form.telefono}
              onChange={(value) => {
                setForm(prev => ({ ...prev, telefono: value }));
              }}
              containerClass="phone-input-profile"
              inputStyle={{
                width: "100%",
                backgroundColor: "transparent", // border & bg handled by wrapper
                color: "#ffffff",
              }}
              buttonStyle={{
                backgroundColor: "transparent",
                border: "none",
              }}
              dropdownStyle={{
                backgroundColor: "#324B8E",
                color: "#ffffff",
              }}
              />
            ) : (
              // all other fields keep normal input
              <input
                type={field.type || "text"}
                name={field.name}
                value={form[field.name]}
                readOnly={field.readOnly}
                maxLength={field.maxLength}
                onChange={e => {
                  const value = field.onlyNumbers
                    ? e.target.value.replace(/\D/g, '')
                    : e.target.value;
                  handleChange({ target: { name: field.name, value } });
                }}
                className={`bg-[#324B8E] w-full text-base leading-6 px-3 py-2 rounded-xl border ${inputBorderClass} mb-4 bg-transparent text-white placeholder-gray-500 outline-none transition ${inputFocusClass}`}
              />
            )}
          </div>
        ))}

        {/* Gender */}
        <div className="mt-6 flex flex-col">
          <label className="text-white mb-2">{translatedText.text17}</label>
          <select
            name="gender"
            value={form.gender}
            onChange={handleChange}
            className={`bg-[#324B8E] w-full text-base leading-6 px-3 py-2 rounded-xl border ${inputBorderClass} mb-4 text-white outline-none transition ${inputFocusClass}`}
          >
            <option value="">--</option>
            <option value="male">{translatedText.textmale}</option>
            <option value="female">{translatedText.textfemale}</option>
            <option value="prefer_not_to_say">{translatedText.textprefer_not_to_say}</option>
          </select>
        </div>

        {/* Preferred Language */}
        <div className="mt-6 flex flex-col">
          <label className="text-white mb-2">{translatedText.text18}</label>
          <select
            name="preferredLanguage"
            value={form.preferredLanguage}
            onChange={handleChange}
            className={`bg-[#324B8E] w-full text-base leading-6 px-3 py-2 rounded-xl border ${inputBorderClass} mb-4 text-white outline-none transition ${inputFocusClass}`}
          >
            <option value="">--</option>
              {Object.entries(LANG_MAP)
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
            ))}
          </select>
        </div>














        {/* Save Button */}
        <div className="md:col-span-2">
          <button
            type="submit"
            className={`text-white text-center font-semibold text-lg mb-6 rounded-xl mt-10 w-full h-10 transition
              ${isGoldUI
                ? 'bg-gradient-to-b from-[#FFD680] to-[#E39B20] shadow-[0_6px_20px_rgba(227,155,32,0.45)] hover:brightness-105'
                : 'bg-[#49B15D] hover:opacity-90'}`}
          >
            {translatedText.text16}
          </button>

        </div>









      </form>
      </>
      )}
      
    </div>
    </>
  );
};

export default Profile;
